<template>
  <div class="do-templates-menu" :class="{ in: show }">
    <div class="border-b border-gray-100 p-4">
      <search-input
        v-model="search"
        no-margin
        no-padding
        name="search-do-templates"
      />
    </div>
    <ul class="max-h-110 overflow-auto p-2">
      <loader :is-loading="loading" />
      <li
        v-for="template in templates"
        :key="template.id"
        v-tooltip="{
          content: template.name,
          placement: 'bottom',
        }"
        class="cursor-pointer truncate p-2 whitespace-nowrap text-gray-700 hover:bg-gray-100"
        @click="emit('select', template)"
      >
        <div class="flex justify-between">
          <p
            class="mr-2 overflow-hidden text-ellipsis whitespace-nowrap"
            data-cy="do-templates-dropdown-option-text"
          >
            {{ template.name }}
          </p>
          <span class="self-center text-xxs text-gray-300">
            {{ formatDateCustom(template.updated_at) }}
          </span>
        </div>
      </li>
      <li v-if="!templates.length" class="px-3 py-2 text-gray-700">
        {{ $t("ORCHESTRATION.NO_TEMPLATES") }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";

import { formatDateCustom } from "@/helpers/formatting";
import { useDeals } from "@/hooks/deals";

import type { IResponseMeta } from "@/models/common";
import type { IOrchestrationTemplate } from "@/models/orchestration";

const { show = false } = defineProps<{
  show?: boolean;
}>();

const emit = defineEmits<{
  select: [IOrchestrationTemplate];
}>();

const { activeDeal, getParamsForRetrievingDOTemplates } = useDeals();
const { dispatch, getters } = useStore();

const templates = ref<IOrchestrationTemplate[]>([]);
const loading = ref(false);
const search = ref("");

const allTemplates = computed<IOrchestrationTemplate[]>(
  () => getters["orchestration/allTemplates"] || []
);

const metadata = computed<IResponseMeta>(() => getters["orchestration/meta"]);

const getTemplates = async (actionName: string) => {
  loading.value = true;

  try {
    const freshTemplates: IOrchestrationTemplate[] = await dispatch(
      actionName,
      getParamsForRetrievingDOTemplates(activeDeal.value, {
        search: search.value,
        forWorkflow: true,
      })
    );

    if (freshTemplates) {
      templates.value = freshTemplates;
    }
  } finally {
    loading.value = false;
  }
};

const debouncedSearch = debounce(
  () => getTemplates("orchestration/fetchAllTemplates"),
  300
);

watch(search, debouncedSearch);

onMounted(() => {
  if (isEmpty(metadata.value)) {
    getTemplates("orchestration/getAllTemplates");
    return;
  }

  templates.value = allTemplates.value;
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.do-templates-menu {
  @apply absolute right-0 w-100 rounded bg-white shadow-lg transition-all;
}

.do-templates-menu:not(.in) {
  top: calc(100% - 0.25rem);
  @apply -z-1 opacity-0;
}

.do-templates-menu.in {
  top: calc(100% + 0.25rem);
  @apply z-40;
}
</style>
